import { ApolloGqlTalentForTalentBoard, TalentBoardComment } from '@bloomays-lib/types.shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type TalentBoardNavState = 'talentboard' | 'resume';

export type TalentBoardState = {
  navState: TalentBoardNavState;
  setNavState: (navState: TalentBoardNavState) => void;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  selectedTalent: ApolloGqlTalentForTalentBoard | null;
  setSelectedTalent: (selectedTalent: ApolloGqlTalentForTalentBoard | null) => void;
  updateSelectedTalentMarkedAsSeen: (markedAsSeen: boolean, dateMarkedAsSeen?: Date | null) => void;
  upsertSelectedTalentComments: (comment: TalentBoardComment) => void;
  deleteSelectedTalentComments: (commentId: string) => void;
  modalOpen: boolean;
  setModalOpen: (modalOpen: boolean) => void;
  modalTab: 'notes' | 'profile' | 'cv';
  setModalTab: (modalTab: 'notes' | 'profile' | 'cv') => void;
  modalContentTab: 'experience' | 'formation';
  setModalContentTab: (modalContentTab: 'experience' | 'formation') => void;
  shareModalOpen: boolean;
  setShareModalOpen: (shareModalOpen: boolean) => void;
  eventsModalOpen: boolean;
  setEventsModalOpen: (eventsModalOpen: boolean) => void;
  previousJobId: string | null;
  setPreviousJobId: (previousJobId: string | null) => void;
  reset: () => void;
};

export const useTalentBoardStore = create<TalentBoardState>()(
  persist(
    (set) => ({
      navState: 'talentboard',
      setNavState: (navState: TalentBoardNavState) => set({ navState }),
      isExpanded: true,
      setIsExpanded: (isExpanded: boolean) => set({ isExpanded }),
      selectedTalent: null,
      setSelectedTalent: (selectedTalent: ApolloGqlTalentForTalentBoard | null) => set({ selectedTalent }),
      updateSelectedTalentMarkedAsSeen: (markedAsSeen: boolean, dateMarkedAsSeen?: Date | null) =>
        set((state) => {
          if (!state.selectedTalent) return state;
          return {
            selectedTalent: {
              ...state.selectedTalent,
              markedAsSeen,
              dateMarkedAsSeen: dateMarkedAsSeen ?? null,
            },
          };
        }),
      upsertSelectedTalentComments: (comment: TalentBoardComment) =>
        set((state) => {
          if (!state.selectedTalent) return state;

          const existingComments = state.selectedTalent.comments || [];
          const commentIndex = existingComments.findIndex((c) => c.id === comment.id);

          let updatedComments: TalentBoardComment[];
          if (commentIndex >= 0) {
            // Mise à jour d'un commentaire existant
            updatedComments = [...existingComments];
            updatedComments[commentIndex] = comment;
          } else {
            // Ajout d'un nouveau commentaire
            updatedComments = [...existingComments, comment];
          }

          return {
            selectedTalent: {
              ...state.selectedTalent,
              comments: updatedComments,
            },
          };
        }),
      deleteSelectedTalentComments: (commentId: string) =>
        set((state) => {
          if (!state.selectedTalent) return state;

          const existingComments = state.selectedTalent.comments || [];
          const updatedComments = existingComments.filter((c) => c.id !== commentId);

          return {
            selectedTalent: {
              ...state.selectedTalent,
              comments: updatedComments,
            },
          };
        }),
      modalOpen: false,
      setModalOpen: (modalOpen: boolean) => set({ modalOpen }),
      modalTab: 'notes',
      setModalTab: (modalTab: 'notes' | 'profile' | 'cv') => set({ modalTab }),
      modalContentTab: 'experience',
      setModalContentTab: (modalContentTab: 'experience' | 'formation') => set({ modalContentTab }),
      shareModalOpen: false,
      setShareModalOpen: (shareModalOpen: boolean) => set({ shareModalOpen }),
      eventsModalOpen: false,
      setEventsModalOpen: (eventsModalOpen: boolean) => set({ eventsModalOpen }),
      previousJobId: null,
      setPreviousJobId: (previousJobId: string | null) => set({ previousJobId }),
      reset: () => set({ navState: 'talentboard', isExpanded: true }),
    }),
    {
      name: 'bloomays-talentboard-store',
      partialize: (state) => ({
        navState: state.navState,
        isExpanded: state.isExpanded,
        selectedTalent: state.selectedTalent,
        modalOpen: state.modalOpen,
        modalTab: state.modalTab,
        modalContentTab: state.modalContentTab,
        shareModalOpen: state.shareModalOpen,
        eventsModalOpen: state.eventsModalOpen,
        previousJobId: state.previousJobId,
      }),
    },
  ),
);

export default useTalentBoardStore;
